import React from 'react'
import allColors from './colors'
import { PENDING_HATCH } from './seriesStyles'

interface Margin {
  top: number
  right: number
  bottom: number
  left: number
}

interface BarStackHorizontalChartProps {
  data: any[]
  minHeight?: number
  xKey: string
  chartSideLabels?: Record<string, string>[]
  colorScaleRange?: string[]
  showLegend?: boolean
  margin?: Margin
}

// A single-row horizontal stacked bar in pure CSS (no chart library): each
// segment's width is its share of the row total. Solid fills - callers pass a
// small set of already-distinct colours, so no texture is needed to tell the
// few segments apart. Optional corner labels (e.g. Current Emission / Target
// Reduction and their values) sit above and below the bar.
// Pending is drawn as thin diagonal lines (the shared PENDING_HATCH) OVER the solid
// segment colour. The hatch is rendered as one overlay per contiguous run of pending
// segments (not per segment), so the lines stay continuous across the differently-
// coloured pending segments instead of resetting at every edge.
const HATCH = PENDING_HATCH

const BarStackHorizontalChart = ({
  data,
  minHeight = 120,
  xKey,
  chartSideLabels,
  colorScaleRange = allColors,
}: BarStackHorizontalChartProps) => {
  const row = (data && data[0]) || {}
  const keys = Object.keys(row).filter((k) => k !== xKey)
  const total = keys.reduce((sum, k) => sum + (Number(row[k]) || 0), 0)

  const label = (idx: number, part: 'key' | 'value') => {
    const entry = chartSideLabels?.[idx]
    if (!entry) return ''
    return part === 'key' ? Object.keys(entry)[0] : (Object.values(entry)[0] as React.ReactNode)
  }

  // Contiguous runs of "pending" segments as [start%, width%] across the bar, so a
  // single hatch overlay can span each run - the diagonal lines then stay continuous
  // across the pending segments instead of resetting at every edge. `boundaries` are
  // the internal segment edges (as %), drawn as thin white dividers (the same white
  // as the hatch) so each block reads as its own.
  const pendingRuns: { start: number; width: number }[] = []
  const boundaries: number[] = []
  const visKeys = keys.filter((k) => (total ? Number(row[k]) || 0 : 0) > 0)
  let cum = 0
  let runStart: number | null = null
  visKeys.forEach((k, idx) => {
    const pct = total ? ((Number(row[k]) || 0) / total) * 100 : 0
    if (/pending/i.test(k)) {
      if (runStart === null) runStart = cum
    } else if (runStart !== null) {
      pendingRuns.push({ start: runStart, width: cum - runStart })
      runStart = null
    }
    cum += pct
    if (idx < visKeys.length - 1) boundaries.push(cum)
  })
  if (runStart !== null) pendingRuns.push({ start: runStart, width: cum - runStart })

  return (
    <div className="flex w-full flex-col justify-center" style={{ minHeight }}>
      <div className="mb-2 flex items-center justify-between text-sm text-gray-700">
        <span>{label(0, 'key')}</span>
        <span>{label(1, 'key')}</span>
      </div>
      <div className="relative flex h-[42px] w-full overflow-hidden rounded-md bg-gray-100" role="img" aria-label="Progress toward target">
        {keys.map((k, i) => {
          const value = Number(row[k]) || 0
          const pct = total ? (value / total) * 100 : 0
          if (pct <= 0) return null
          return (
            <div
              key={k}
              className="h-full"
              style={{ width: `${pct}%`, backgroundColor: colorScaleRange[i % colorScaleRange.length] }}
              title={`${k}: ${value.toLocaleString()} tCO₂e`}
            />
          )
        })}
        {/* One continuous hatch per pending run, so the diagonal lines align across
            the pending segments while each segment colour still shows in the gaps. */}
        {pendingRuns.map((run, idx) => (
          <div
            key={`hatch-${idx}`}
            className="pointer-events-none absolute inset-y-0"
            style={{ left: `${run.start}%`, width: `${run.width}%`, backgroundImage: HATCH }}
          />
        ))}
        {/* Thin light seam between blocks (the same white as the hatch, a touch
            softer) so each block reads apart without a harsh line. */}
        {boundaries.map((pos, idx) => (
          <div
            key={`bnd-${idx}`}
            className="pointer-events-none absolute inset-y-0"
            style={{ left: `${pos}%`, width: '1.5px', transform: 'translateX(-0.75px)', backgroundColor: 'rgba(255,255,255,0.65)' }}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-sm font-medium text-gray-800">
        <span>{label(0, 'value')}</span>
        <span>{label(1, 'value')}</span>
      </div>
    </div>
  )
}

export default BarStackHorizontalChart
